import React from "react";
import { Select, SelectItem, TextArea, TextInput } from "@carbon/react";
import { useIntl } from "react-intl";
import ResultMultiSelect from "../../common/multiSelect";
import CascadingMultiSelect from "../../common/cascadingMultiSelect";
import {
  decimalPlaces,
  exceedsDecimalPlaces,
  normalizeScientificNotation,
} from "../scientificNotation";

/**
 * OGC-1020 (R1) — FR-A1 polymorphic result cell.
 *
 * Renders the result input by the test's result type — numeric (N),
 * dictionary (D), multi-checkbox (M) — matching the legacy widgets one for
 * one so stored values stay compatible. Cascading (C), remark (R) and
 * alphanumeric (A) reuse the legacy behavior. Titer (T) is a free-text
 * dilution ratio such as 1:10, stored as typed (OGC-1185). When the row is
 * read-only (FR-A2: saved until Edit) the stored display value renders as
 * plain text.
 *
 * A multi-component test yields one row PER COMPONENT sharing an analysisId
 * (FR-A′1), so widget identity and change events are keyed by the composite
 * row key — never by analysisId alone.
 */

export interface DictionaryOption {
  id: string;
  value: string;
}

export interface ResultCellRow {
  id: string;
  analysisId: string;
  testResultComponentId?: string;
  resultType: string;
  resultValue?: string;
  /** the value as stored, where resultValue is the value as reported */
  rawResultValue?: string;
  multiSelectResultValues?: string;
  dictionaryResults?: DictionaryOption[];
  unitsOfMeasure?: string;
  testName?: string;
  /** decimal places this test is configured to report to */
  reportingDecimalPlaces?: number;
}

/**
 * The decimal places entered, or 0 when the value carries no fraction. A value
 * in scientific notation (1.5e5, 1.5×10⁵) is judged by its mantissa: 1.5e5
 * carries one place, not three.
 *
 * <p>A value entered to more places than the test reports to is stored in full
 * and shown rounded, so the record and the screen stop agreeing and the next
 * edit saves the rounded form over the stored one (OGC-1179). The entry field
 * says so rather than accepting it silently. A whole-number test does not
 * constrain a mantissa: 1.5e5 is a whole number.
 */
export function enteredDecimalPlaces(value: string): number {
  return decimalPlaces(value);
}

export function exceedsReportingDecimalPlaces(
  value: string | undefined,
  reportingDecimalPlaces: number | undefined,
): boolean {
  if (!value || reportingDecimalPlaces == null || reportingDecimalPlaces < 0) {
    return false;
  }
  return (
    normalizeScientificNotation(value) === value &&
    !/[eE]/.test(value) &&
    Number.isFinite(Number(value)) &&
    exceedsDecimalPlaces(value, reportingDecimalPlaces)
  );
}

/**
 * Whether the row is holding a newly typed value finer than the test reports
 * to, which the entry field warns about. The value is saved as typed and
 * reported rounded to the test's places (OGC-1544), so it is not refused.
 *
 * <p>A value already stored at that precision raises no warning: it is a
 * record that exists.
 */
export function warnsOnReportingFormat(row: {
  resultType?: string;
  resultValue?: string;
  rawResultValue?: string;
  reportingDecimalPlaces?: number;
}): boolean {
  return (
    row.resultType === "N" &&
    row.resultValue !== row.rawResultValue &&
    exceedsReportingDecimalPlaces(row.resultValue, row.reportingDecimalPlaces)
  );
}

/** Unique identity for a worklist row: one analysis may render N component rows. */
export function worklistRowKey(row: {
  analysisId: string;
  testResultComponentId?: string;
}): string {
  return `${row.analysisId}-${row.testResultComponentId || "primary"}`;
}

interface PolymorphicResultCellProps {
  row: ResultCellRow;
  editable: boolean;
  onValueChange: (
    field: "resultValue" | "multiSelectResultValues",
    value: string,
  ) => void;
  /** OGC-1417: the numeric field was left, so its value can be judged */
  onValueBlur?: (nextFocus: EventTarget | null) => void;
}

function readOnlyDisplay(row: ResultCellRow): string {
  if (row.resultType === "D") {
    const match = (row.dictionaryResults || []).find(
      (option) => option.id === row.resultValue,
    );
    return match ? match.value : row.resultValue || "";
  }
  if (row.resultType === "M" || row.resultType === "C") {
    try {
      const parsed = JSON.parse(row.multiSelectResultValues || "{}");
      const ids = Object.values(parsed)
        .flatMap((group) => String(group).split(","))
        .filter(Boolean);
      return (row.dictionaryResults || [])
        .filter((option) => ids.includes(String(option.id)))
        .map((option) => option.value)
        .join(", ");
    } catch {
      return "";
    }
  }
  return row.resultValue || "";
}

const PolymorphicResultCell: React.FC<PolymorphicResultCellProps> = ({
  row,
  editable,
  onValueChange,
  onValueBlur,
}) => {
  const intl = useIntl();
  const rowKey = worklistRowKey(row);
  // The cell is the page's primary control and carries no visible label — the
  // column header names it for a sighted reader, but a screen reader lands on
  // an unnamed control, so each variant gets a hidden one (OGC-1179).
  const accessibleName = intl.formatMessage(
    { id: "label.results.resultFor" },
    { 0: row.testName || "" },
  );

  if (!editable) {
    return (
      <span className="unifiedResultsReadOnlyValue">
        {readOnlyDisplay(row)}
        {row.resultType === "N" &&
          row.rawResultValue != null &&
          row.rawResultValue !== row.resultValue && (
            <span className="cds--label">
              {intl.formatMessage(
                { id: "label.results.clinicalValue" },
                { value: row.rawResultValue },
              )}
            </span>
          )}
      </span>
    );
  }

  switch (row.resultType) {
    case "D":
      return (
        <Select
          id={`unifiedResultValue-${rowKey}`}
          name={`unifiedResultValue-${rowKey}`}
          labelText={accessibleName}
          hideLabel
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onValueChange("resultValue", e.target.value)
          }
          value={row.resultValue || ""}
        >
          <SelectItem text="" value="" />
          {(row.dictionaryResults || []).map((option) => (
            <SelectItem text={option.value} value={option.id} key={option.id} />
          ))}
        </Select>
      );

    case "M":
      return (
        <ResultMultiSelect
          id={`unifiedMultiResultValue-${rowKey}`}
          name={`unifiedMultiResultValue-${rowKey}`}
          dictionaryValues={row.dictionaryResults || []}
          value={row.multiSelectResultValues}
          onChange={(e: { target: { value: string } }) =>
            onValueChange("multiSelectResultValues", e.target.value)
          }
        />
      );

    case "C":
      return (
        <CascadingMultiSelect
          id={`unifiedCascadingResultValue-${rowKey}`}
          name={`unifiedCascadingResultValue-${rowKey}`}
          dictionaryValues={row.dictionaryResults || []}
          value={row.multiSelectResultValues}
          onChange={(e: { target: { value: string } }) =>
            onValueChange("multiSelectResultValues", e.target.value)
          }
        />
      );

    case "N": {
      const formatWillChange = warnsOnReportingFormat(row);
      return (
        <TextInput
          id={`unifiedResultValue-${rowKey}`}
          labelText={accessibleName}
          hideLabel
          type="text"
          inputMode="text"
          warn={formatWillChange}
          warnText={intl.formatMessage(
            { id: "warning.results.reportingFormat" },
            { 0: row.reportingDecimalPlaces ?? 0 },
          )}
          value={row.resultValue || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onValueChange("resultValue", e.target.value)
          }
          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
            onValueBlur?.(e.relatedTarget)
          }
        />
      );
    }

    case "T":
      return (
        <TextInput
          id={`unifiedResultValue-${rowKey}`}
          labelText={accessibleName}
          hideLabel
          type="text"
          placeholder={intl.formatMessage({
            id: "label.results.titer.placeholder",
          })}
          value={row.resultValue || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onValueChange("resultValue", e.target.value)
          }
        />
      );

    case "R":
    case "A":
      return (
        <TextArea
          id={`unifiedResultValue-${rowKey}`}
          rows={1}
          labelText={accessibleName}
          hideLabel
          value={row.resultValue || ""}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onValueChange("resultValue", e.target.value)
          }
        />
      );

    default:
      return <span>{row.resultValue || ""}</span>;
  }
};

export default PolymorphicResultCell;
